import { useCallback } from 'react';
import { isEmpty } from 'lodash';
import { GPC, ValuationApproachGPC } from 'api';
import { parseGpcComparison } from 'pages/Valuations/approaches/guidelinePublicCompanies/gpc/config/utilities';
import {
  GPCConfigurationSpreadsheets,
  GPCExtended,
  ValuationApproachGpcCompGroupExtended,
  ValuationApproachGPCExtended,
  ValuationApproachModelExtended,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/GuidelinePublicCompanies.props';
import makeCapIQDataFlatWithDecimalPlaces from 'pages/Valuations/approaches/guidelinePublicCompanies/utils/makeCapIQDataFlatWithDecimalPlaces';
import { GPCAttributes } from 'pages/Valuations/approaches/guidelinePublicCompanies/utils/types';
import { CapIQCompanyData, PublicComp } from 'pages/ValuationsAllocation/types';
import { getObjectValue } from 'utilities';
import { UseSyncGpcPublicCompRowOrderResult } from '../useSyncGpcPublicCompRowOrder';
import {
  buildApproachUpdatePayload,
  cleanupCompGroupAssociations,
  filterAndReindexComparisons,
  resetDefaultMetricsEnablement,
  trackDeletedComparisons,
} from './helpers';

const useCompsActions = ({
  approach,
  updateApproach,
  combinedGPCComparisonPerfMetricAttrs,
  orderGpcComparisonsForAdd,
  gpcSpreadsheet,
  setAreThereChanges,
  removedGPCsRef,
}: {
  gpcSpreadsheet: GPCConfigurationSpreadsheets;
  combinedGPCComparisonPerfMetricAttrs: GPCAttributes;
  orderGpcComparisonsForAdd: UseSyncGpcPublicCompRowOrderResult['orderGpcComparisonsForAdd'];
  approach: ValuationApproachModelExtended;
  updateApproach: (
    approachGpc: any,
    tableData: any,
    updatedSortedColumn?: object,
    updateDependencies?: boolean
  ) => void;
  setAreThereChanges?: React.Dispatch<React.SetStateAction<boolean>> | null;
  removedGPCsRef: React.RefObject<{ capitalIQId: string; gpcComparisonsId?: number }[]>;
}) => {
  const mergeNewCompInApproach = useCallback(
    ({ gpc }: { gpc: PublicComp }) => {
      // early return if comp is already in approach
      if (
        (approach.valuations_approach_gpc?.gpc_comparison as GPC[])?.findIndex(
          comparison => comparison.cap_iq_id === gpc?.cap_iq_id?.value
        ) >= 0
      ) {
        return null;
      }

      const flattened = makeCapIQDataFlatWithDecimalPlaces({
        data: gpc,
        attributes: combinedGPCComparisonPerfMetricAttrs,
      });
      resetDefaultMetricsEnablement(flattened);
      flattened.name = flattened.company_name;
      const { calendar_years_financials: calendarYearsFinancials } = parseGpcComparison({
        data: gpc,
        attributes: combinedGPCComparisonPerfMetricAttrs,
      }) as GPC;
      flattened.calendar_years_financials = calendarYearsFinancials;
      flattened.reference_for_backsolve = flattened.cap_iq_id;

      return {
        ...approach,
        valuations_approach_gpc: {
          ...approach.valuations_approach_gpc,
          gpc_comparison: [...(approach.valuations_approach_gpc?.gpc_comparison || []), flattened] as GPC[],
        },
      };
    },
    [approach, combinedGPCComparisonPerfMetricAttrs]
  );

  const onAddComp = useCallback(
    async (gpcData: CapIQCompanyData[]) => {
      // To make the implementation of onAdd here and in the comp groups consistent, the argument
      // passed to the function is an array (even when it contains a single object in this case)
      const { gpc } = gpcData[0];
      const updatedApproach = mergeNewCompInApproach({ gpc: gpc! });
      if (updatedApproach) {
        const { valuations_approach_gpc: approachGpc } = updatedApproach;

        const { tableData } = gpcSpreadsheet;

        const { orderedComparisons, sortedColumnForUi } = orderGpcComparisonsForAdd(
          approachGpc.gpc_comparison as GPCExtended[]
        );
        const gpcApproachWithOrder = {
          ...(approachGpc as ValuationApproachGPC),
          gpc_comparison: orderedComparisons.map((comp, index) => ({
            ...comp,
            order: index,
          })) as GPCExtended[],
        } as ValuationApproachGPCExtended;

        tableData.approach.valuations_approach_gpc = gpcApproachWithOrder;
        updateApproach(gpcApproachWithOrder, tableData, sortedColumnForUi, true);
      }
    },
    [updateApproach, mergeNewCompInApproach, gpcSpreadsheet, orderGpcComparisonsForAdd]
  );

  /**
   * Batch-delete variant of onRemove: removes multiple GPC comparisons in a single operation.
   */
  const onRemoveMultipleComps = useCallback(
    async (deletedComparisons: GPCExtended[]) => {
      if (!deletedComparisons.length) {
        return;
      }

      const { valuations_approach_gpc: gpc } = approach;
      const currentDeletedCompGroups = gpc?.deleted_comp_groups ?? [];

      const deletedCapIqIds = new Set(deletedComparisons.map(c => c.cap_iq_id));
      const deletedCompGroupIds = deletedComparisons
        .map(c => c.comp_group_id ?? c.gpc_approach_comp_group)
        .filter(Boolean) as number[];
      const deletedCompGroupIdSet = new Set(deletedCompGroupIds);
      const alreadyRemovedCapIqIds = new Set(removedGPCsRef.current.map(item => item.capitalIQId));

      const filteredComparisons = filterAndReindexComparisons(
        approach?.valuations_approach_gpc?.gpc_comparison ?? [],
        deletedCapIqIds,
        alreadyRemovedCapIqIds
      );

      cleanupCompGroupAssociations(filteredComparisons, deletedCompGroupIdSet);

      const deletedApproachCompGroups = new Set(deletedComparisons.map(c => c.gpc_approach_comp_group).filter(Boolean));
      const compGroupsToRemove = new Set(
        gpc?.valuationapproachgpccompgroup_set
          ?.filter(item => deletedApproachCompGroups.has(item.id))
          .map(item => item.comp_group) ?? []
      );
      const valuationKeysToSave = gpc?.valuationapproachgpccompgroup_set?.filter(
        item => !compGroupsToRemove.has(item.comp_group)
      );

      trackDeletedComparisons(removedGPCsRef, deletedComparisons);

      const newDeletedCompGroups = deletedComparisons
        .filter(c => c.gpc_approach_comp_group)
        .map(c => c.comp_group_id ?? c.gpc_approach_comp_group)
        .filter(Boolean) as number[];

      const { tableData } = gpcSpreadsheet;
      tableData.approach.valuations_approach_gpc = buildApproachUpdatePayload(
        gpc,
        filteredComparisons,
        valuationKeysToSave ?? [],
        currentDeletedCompGroups,
        newDeletedCompGroups,
        removedGPCsRef,
        approach.id
      );

      updateApproach(
        { ...gpc, gpc_comparison: filteredComparisons, valuationapproachgpccompgroup_set: valuationKeysToSave },
        tableData,
        {},
        true
      );
      setAreThereChanges?.(true);
    },
    [updateApproach, approach, gpcSpreadsheet, removedGPCsRef, setAreThereChanges]
  );

  const onRemoveComp = useCallback(
    async (deletedComparison: GPCExtended) => {
      // Normalize to array for consistent processing with onRemoveMultipleComps
      const deletedComparisons = [deletedComparison];

      onRemoveMultipleComps(deletedComparisons);
    },
    [onRemoveMultipleComps]
  );

  const getFlattenedItem = useCallback(
    (companyItem: CapIQCompanyData) => {
      const { gpc } = companyItem;
      const { comp_group_id, comp_group_name, ...newGpc } = getObjectValue(gpc);
      const flattened = makeCapIQDataFlatWithDecimalPlaces({
        data: newGpc,
        attributes: combinedGPCComparisonPerfMetricAttrs,
      });
      resetDefaultMetricsEnablement(flattened);
      flattened.reference_for_backsolve = flattened.cap_iq_id;
      flattened.name = flattened.company_name;
      const { calendar_years_financials: calendarYearsFinancials } = parseGpcComparison({
        data: gpc!,
        attributes: combinedGPCComparisonPerfMetricAttrs,
      }) as GPC;
      flattened.calendar_years_financials = calendarYearsFinancials;

      if (comp_group_id && comp_group_name) {
        flattened.comp_group_id = comp_group_id as string;
        flattened.comp_group_name = comp_group_name as string;
        flattened.temp_ref = comp_group_id as string;
      }
      return flattened as unknown as GPCExtended;
    },
    [combinedGPCComparisonPerfMetricAttrs]
  );

  const getCompGroupsData = useCallback(
    (comparisons: GPCExtended[], compGroupToDelete: number) => {
      const { tableData } = gpcSpreadsheet;
      const { valuationapproachgpccompgroup_set } = getObjectValue(tableData.approach.valuations_approach_gpc);
      const compGroupsData =
        valuationapproachgpccompgroup_set?.filter(item => compGroupToDelete !== item.comp_group) || [];
      const newCompGroups = comparisons
        .filter((comparison: GPCExtended) => comparison.comp_group_id && compGroupToDelete !== comparison.comp_group_id)
        .filter((item: GPCExtended) => !compGroupsData.some(compGroupData => compGroupData.id === item.comp_group_id))
        .map((comparisonCompGroupItem: GPCExtended) => ({
          isNew: true,
          comp_group: comparisonCompGroupItem.comp_group_id,
          comp_group_name: comparisonCompGroupItem.comp_group_name,
          use_latest_comp_group_version: true,
        }));
      const uniqueNewCompGroupsData = [...new Map(newCompGroups.map((item: any) => [item.comp_group, item])).values()];
      return [...compGroupsData, ...uniqueNewCompGroupsData];
    },
    [gpcSpreadsheet]
  );

  const getComparisonsData = useCallback(
    (
      comparisons: GPCExtended[],
      comparisonsToDelete: number[],
      compGroupsData: GPCExtended[],
      useLatestVersion: boolean
    ) => {
      const { tableData } = gpcSpreadsheet;
      const { gpc_comparison } = getObjectValue(tableData.approach.valuations_approach_gpc);
      const remainingTransactions = (gpc_comparison ?? []).filter(
        currentTransaction => !comparisonsToDelete.includes(Number(currentTransaction.id ?? currentTransaction.temp_id))
      );
      const comparisonsWithUpdatedCompGroup = remainingTransactions.map(comparison => {
        const transactionCompGroupId = comparison.gpc_approach_comp_group ?? comparison.comp_group_id;
        const hasCompGroup = compGroupsData.some(compGroup => compGroup.id === transactionCompGroupId);
        if (!hasCompGroup) {
          const { gpc_approach_comp_group, comp_group_id, comp_group_name, temp_ref, ...newTransactionObj } =
            comparison;
          return newTransactionObj;
        }

        return comparison;
      });
      comparisons.forEach(comparison => {
        const currentTransactionIdx = comparisonsWithUpdatedCompGroup.findIndex(
          current => current.cap_iq_id === comparison.cap_iq_id
        );
        const found = currentTransactionIdx !== -1;

        if (found) {
          const currentTransactionData = comparisonsWithUpdatedCompGroup[currentTransactionIdx] as GPCExtended;
          if (useLatestVersion) {
            comparisonsWithUpdatedCompGroup[currentTransactionIdx] = {
              id: currentTransactionData.id,
              ...comparison,
            };
          } else {
            const { comp_group_id, comp_group_name, temp_ref, gpc_approach_comp_group, ...updatedCurrentTransaction } =
              getObjectValue(currentTransactionData);
            comparisonsWithUpdatedCompGroup[currentTransactionIdx] = {
              id: currentTransactionData.id,
              ...updatedCurrentTransaction,
            };
          }
        } else {
          comparisonsWithUpdatedCompGroup.push(comparison);
        }
      });
      return comparisonsWithUpdatedCompGroup;
    },
    [gpcSpreadsheet]
  );

  const getDeletedCompGroupsIDs = useCallback(
    (compGroupToDelete: number) => {
      const { tableData } = gpcSpreadsheet;
      const { valuationapproachgpccompgroup_set } = getObjectValue(tableData.approach.valuations_approach_gpc);
      const findMatch = (compGroupItem: ValuationApproachGpcCompGroupExtended) =>
        compGroupToDelete === compGroupItem.comp_group;
      return (valuationapproachgpccompgroup_set ?? [])
        .filter(compGroupItem => !compGroupItem.isNew && findMatch(compGroupItem))
        .map(compGroupItem => compGroupItem.id);
    },
    [gpcSpreadsheet]
  );

  /**
   * Executes when adding comps through the selection of a comp group
   */
  const saveCompGroups = useCallback(
    async (
      comparisons: CapIQCompanyData[],
      useLatestVersion: boolean | undefined,
      comparisonsToDelete: number[],
      compGroupToDelete: number
    ) => {
      const { tableData } = gpcSpreadsheet;
      const validComparisons = comparisons.filter(item => item.valid_gpc_results);

      const flattenedComparisons = validComparisons.map(companyItem => getFlattenedItem(companyItem));
      const compGroupsData = getCompGroupsData(flattenedComparisons, compGroupToDelete) as GPCExtended[];
      const gpcComparison = getComparisonsData(
        flattenedComparisons,
        comparisonsToDelete,
        compGroupsData,
        Boolean(useLatestVersion)
      );

      const valuationApproachGPC = {
        ...tableData.approach.valuations_approach_gpc,
        gpc_comparison: gpcComparison,
        valuationapproachgpccompgroup_set: compGroupsData,
      };

      if (!isEmpty(comparisonsToDelete)) {
        valuationApproachGPC.deleted_gpc_comparisons = comparisonsToDelete;
      }
      if (compGroupToDelete) {
        const deletedCompGroups = getDeletedCompGroupsIDs(compGroupToDelete);
        valuationApproachGPC.deleted_comp_groups = deletedCompGroups;
      }
      tableData.approach.valuations_approach_gpc = valuationApproachGPC as unknown as ValuationApproachGPCExtended;
      updateApproach(valuationApproachGPC, tableData, {}, true);
    },
    [getCompGroupsData, getComparisonsData, getDeletedCompGroupsIDs, gpcSpreadsheet, updateApproach, getFlattenedItem]
  );

  return {
    onAddComp,
    onRemoveComp,
    onRemoveMultipleComps,
    saveCompGroups,
  };
};

export default useCompsActions;
